import React, { FC, useMemo } from 'react';
import { Checkbox, IconButton, Link, Box } from '@mui/material';
import DataTableTimeInput from 'components/shared/data-table/data-table-time-input.component';
import DataTableAutocomplete, {
    DataTableAutocompleteOption,
} from 'components/shared/data-table/data-table-autocomplete.component';
import { DataTableCell, DataTableValue } from 'components/shared/data-table/data-table.componet';
import DataTableDateTimePicker from 'components/shared/data-table/data-table-date-time-picker.component';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Draggable } from '@hello-pangea/dnd';
import { ProductionPlanningStep } from 'models/production-planning';
import { Machine } from 'models/organization';
import { useTranslation } from 'react-i18next';

type ProductionSchedulingRowProps = {
    row: ProductionPlanningStep;
    index: number;
    departmentName: string;
    dayNumber: number;
    totalDepartmentHours: number;
    isOverLimit: boolean;
    isStartDateInvalid: boolean;
    isDeadlineInvalid: boolean;
    isProductionHoursInvalid?: boolean;
    isMachineHoursInvalid?: boolean;
    machines: Machine[];
    onStepChange: (index: number, update: Partial<ProductionPlanningStep>, options?: { immediate?: boolean }) => void;
    onDeleteStep: (index: number) => void;
    onMoveStepToNextDay: (index: number) => Promise<void>;
    onProductionHoursValidChange?: (index: number, isValid: boolean) => void;
    onMachineHoursValidChange?: (index: number, isValid: boolean) => void;
    onProductionHoursBlur?: (index: number) => void;
    onMachineHoursBlur?: (index: number) => void;
};

const ProductionSchedulingRow: FC<ProductionSchedulingRowProps> = ({
    row,
    index,
    departmentName,
    dayNumber,
    totalDepartmentHours,
    isOverLimit,
    isStartDateInvalid,
    isDeadlineInvalid,
    isProductionHoursInvalid,
    isMachineHoursInvalid,
    machines,
    onStepChange,
    onDeleteStep,
    onMoveStepToNextDay,
    onProductionHoursValidChange,
    onMachineHoursValidChange,
    onProductionHoursBlur,
    onMachineHoursBlur,
}) => {
    const draggableId = row.stepId || `step-${index}`;
    const { t: translate } = useTranslation(['orders']);

    const machineOptions: DataTableAutocompleteOption[] = useMemo(
        () =>
            machines.map((m) => ({
                id: m._id,
                key: m.machineSchedule?.length ? `${m.name} (${m.machineSchedule.join(', ')})` : m.name,
            })),
        [machines],
    );

    return (
        <Draggable draggableId={draggableId} index={index}>
            {(provided) => (
                <tr ref={provided.innerRef} {...provided.draggableProps}>
                    <DataTableCell readonly {...provided.dragHandleProps} sx={{ width: 0, padding: 0 }}>
                        <DragIndicatorIcon fontSize="small" style={{ marginTop: '5px' }} />
                    </DataTableCell>
                    <DataTableCell readonly>
                        <DataTableValue>
                            <Box display="flex">
                                <Box flexGrow={1}>
                                    {departmentName ? `${departmentName} - ${row.stepName}` : row.stepName}
                                </Box>
                                <Link
                                    component="button"
                                    variant="caption"
                                    onClick={() => onMoveStepToNextDay(index)}
                                    sx={{
                                        textDecoration: 'none',
                                        whiteSpace: 'nowrap',
                                        flexShrink: 0,
                                        '&:hover': { textDecoration: 'underline' },
                                    }}
                                >
                                    +1 {translate('orders:DAY')}
                                </Link>
                            </Box>
                        </DataTableValue>
                    </DataTableCell>
                    <DataTableCell error={isProductionHoursInvalid}>
                        <DataTableTimeInput
                            value={row.estimatedProductionHours}
                            onChange={(value) => onStepChange(index, { estimatedProductionHours: value })}
                            onValidChange={(isValid) => onProductionHoursValidChange?.(index, isValid)}
                            onBlur={() => onProductionHoursBlur?.(index)}
                        />
                    </DataTableCell>
                    <DataTableCell>
                        <DataTableAutocomplete
                            options={machineOptions}
                            selectedValue={machineOptions.find((m) => m.id === row.machineId) ?? null}
                            onSelectValue={(selected) => {
                                const machine = selected ? machines.find((m) => m._id === selected.id) : null;
                                onStepChange(
                                    index,
                                    {
                                        machineId: machine?._id || undefined,
                                        estimatedMachineHours: machine ? undefined : row.estimatedMachineHours,
                                    },
                                    { immediate: true },
                                );
                            }}
                            width={300}
                        />
                    </DataTableCell>
                    <DataTableCell error={isMachineHoursInvalid}>
                        <DataTableTimeInput
                            value={
                                row.machineId
                                    ? machines.find((m) => m._id === row.machineId)?.defaultMachineTime ?? 0
                                    : row.estimatedMachineHours
                            }
                            onChange={(value) => onStepChange(index, { estimatedMachineHours: value })}
                            onValidChange={(isValid) => onMachineHoursValidChange?.(index, isValid)}
                            onBlur={() => onMachineHoursBlur?.(index)}
                            disabled={!!row.machineId}
                        />
                    </DataTableCell>
                    <DataTableCell readonly>
                        <DataTableValue
                            sx={{
                                color: isOverLimit ? '#d32f2f' : 'text.secondary',
                                fontWeight: isOverLimit ? 600 : 400,
                            }}
                        >
                            {totalDepartmentHours > 0 ? `${totalDepartmentHours.toFixed(1)}h` : '-'}
                        </DataTableValue>
                    </DataTableCell>
                    <DataTableCell warning={isStartDateInvalid}>
                        <DataTableDateTimePicker
                            value={row.startDate}
                            onChange={(date: Date | null) =>
                                onStepChange(index, { startDate: date }, { immediate: true })
                            }
                        />
                    </DataTableCell>
                    <DataTableCell warning={isDeadlineInvalid}>
                        <DataTableDateTimePicker
                            value={row.deadline}
                            onChange={(date: Date | null) =>
                                onStepChange(index, { deadline: date }, { immediate: true })
                            }
                        />
                    </DataTableCell>
                    <DataTableCell align="center">
                        <Checkbox
                            checked={row.allowOvernight || false}
                            onChange={(e) =>
                                onStepChange(index, { allowOvernight: e.target.checked }, { immediate: true })
                            }
                            size="small"
                            sx={{ padding: '2px', transform: 'scale(0.85)' }}
                        />
                    </DataTableCell>
                    <DataTableCell align="center">
                        <Checkbox
                            checked={row.startNextDay || false}
                            onChange={(e) =>
                                onStepChange(index, { startNextDay: e.target.checked }, { immediate: true })
                            }
                            size="small"
                            sx={{ padding: '2px', transform: 'scale(0.85)' }}
                        />
                    </DataTableCell>
                    <DataTableCell align="center" readonly>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                            <DataTableValue>{dayNumber}</DataTableValue>
                        </Box>
                    </DataTableCell>
                    <DataTableCell align="center">
                        <IconButton size="small" onClick={() => onDeleteStep(index)}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </DataTableCell>
                </tr>
            )}
        </Draggable>
    );
};

export default ProductionSchedulingRow;
